import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardSummary } from '../../services/dashboardService';
import { DashboardSummary } from '../../interfaces/dashboard.types';
import styles from './DashboardPage.module.css';
import { Briefcase, CheckSquare, CalendarClock, Users, FileText, Plus, AlertTriangle, RefreshCw } from 'lucide-react';

// --- Placeholder para los iconos de KPI ---
const kpiIcons: { [key: string]: React.ElementType } = {
    casosActivos: Briefcase,
    tareasPendientes: CheckSquare,
    proximasAudienciasCount: CalendarClock,
    clientesTotales: Users
};

const DashboardPage: React.FC = () => {
    const { user } = useAuth();
    const [summary, setSummary] = useState<DashboardSummary | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDashboardData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getDashboardSummary();
            setSummary(data);
        } catch (err: any) {
            setError(err.message || 'No se pudo cargar la información del dashboard.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    if (isLoading) {
        return <div className={styles.loadingState}>Cargando información del dashboard...</div>;
    }

    if (error) {
        return (
            <div className={styles.errorState}>
                <AlertTriangle size={48} />
                <h2>Ocurrió un Error</h2>
                <p>{error}</p>
                <button onClick={fetchDashboardData} className={styles.retryButton}>
                    <RefreshCw size={16} />
                    <span>Reintentar</span>
                </button>
            </div>
        );
    }

    return (
        <div className={styles.dashboardContainer}>
            {/* --- Encabezado --- */}
            <header className={styles.header}>
                <div>
                    <h1 className={styles.title}>Bienvenido, {user?.nombre_completo || 'Abogado'}</h1>
                    <p className={styles.subtitle}>Aquí tienes un resumen de tu actividad y próximos eventos.</p>
                </div>
                <Link to="/gestion/casos/nuevo" className={styles.createCaseButton}>
                    <Plus size={20} />
                    <span>Crear Nuevo Caso</span>
                </Link>
            </header>

            {/* --- Tarjetas de KPI (Indicadores Clave) --- */}
            <div className={styles.kpiGrid}>
                {summary && Object.entries(summary.kpis).map(([key, value]) => {
                    const Icon = kpiIcons[key];
                    // Formatear el título para que sea legible
                    const title = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                        <div key={key} className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper}>
                                {Icon && <Icon size={24} />}
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiValue}>{value}</span>
                                <span className={styles.kpiTitle}>{title}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- Contenido Principal (Casos y Actividades) --- */}
            <main className={styles.mainContent}>
                {/* --- Columna de Casos Recientes --- */}
                <div className={styles.contentCard}>
                    <h2 className={styles.cardTitle}>Casos Actualizados Recientemente</h2>
                    <div className={styles.caseList}>
                        {summary?.casosRecientes.map(caso => (
                            <Link to={`/gestion/casos/detail/${caso.id}`} key={caso.id} className={styles.caseItem}>
                                <div className={styles.caseIcon}>
                                    <Briefcase size={20} />
                                </div>
                                <div className={styles.caseDetails}>
                                    <span className={styles.caseName}>{caso.tipo_caso}</span>
                                    <span className={styles.caseClient}>{caso.nombre_completo}</span>
                                </div>
                                <span className={`${styles.caseStatus} ${styles[`status${caso.estado_caso.replace(' ', '')}`]}`}>
                                    {caso.estado_caso}
                                </span>
                            </Link>
                        ))}
                         {summary?.casosRecientes.length === 0 && <p className={styles.emptyState}>No hay casos activos recientes.</p>}
                    </div>
                </div>

                {/* --- Columna de Próximas Actividades --- */}
                <div className={styles.contentCard}>
                    <h2 className={styles.cardTitle}>Agenda de Próximas Diligencias</h2>
                    <div className={styles.activityList}>
                        {summary?.agendaDelDia.map((activity) => (
                            <div key={activity.id} className={styles.activityItem}>
                                <span className={styles.activityTime}>{new Date(activity.fecha_hora).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}</span>
                                <div className={styles.activitySeparator}></div>
                                <div className={styles.activityDetails}>
                                    <span className={styles.activityTitle}>{activity.descripcion}</span>
                                    <span className={styles.activityClient}>Caso: {activity.nombre_cliente}</span>
                                </div>
                            </div>
                        ))}
                        {summary?.agendaDelDia.length === 0 && <p className={styles.emptyState}>No hay diligencias programadas.</p>}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default DashboardPage;